/**
 * DeliveryPage Component
 * 배달 통합 페이지 - 요약/상세 탭
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../../core/components/BottomNavigation';
import BalanceDisplay from './shared/BalanceDisplay';
import { DeliverySummaryTab } from './DeliverySummaryTab';
import { DeliveryDetailTab } from './DeliveryDetailTab';
import { getUserInfo } from '../../../../core/utils';
import { useAuth } from '../../../../core/contexts/AuthContext';
import HomePageForSupplier from '../../components/HomePageForSupplier';
import MaintenanceScreen from '../../../system/maintenance/components/MaintenanceScreen';
import NoticePopupModal from '../../../system/notice/components/NoticePopupModal';
import { getMobileUnreadPopupNotices, markMobileNoticeAsRead } from '../../../system/notice/api/noticeApi';
import { NoticeResult } from '../../../system/notice/types/notice.types';
import { useMaintenanceContext } from '../../../system/maintenance/contexts/MaintenanceContext';
import styles from '../styles/DeliveryPage.module.css';

type TabType = 'summary' | 'detail';

const DeliveryPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabType>('summary');
    const { activeMaintenance, isMaintenance, loading: maintenanceLoading } = useMaintenanceContext();

    // 협력사 판단 로직 - userInfo의 prmCode로 판단 (R: 라이더, S: 협력사, A: 관리자)
    const userInfo = getUserInfo();
    const isSupplier = userInfo?.prmCode === 'S';
    const { loading } = useAuth();

    const riderName = useMemo(() => {
        return userInfo?.riderName || '사용자';
    }, [userInfo]);

    // 공지사항 팝업 상태
    const [popupNotices, setPopupNotices] = useState<NoticeResult[]>([]);
    const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

    // 점검 화면 확인 여부 (세션당 한 번만 표시)
    const [maintenanceAcknowledged, setMaintenanceAcknowledged] = useState(() => {
        const acknowledged = sessionStorage.getItem('maintenance_acknowledged');
        const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
        // 점검 ID가 변경되었으면 확인 상태 초기화
        if (activeMaintenance && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
            sessionStorage.removeItem('maintenance_acknowledged');
            return false;
        }
        return acknowledged === 'true';
    });

    // 점검 상태가 변경되면 확인 상태 초기화
    useEffect(() => {
        if (!isMaintenance) {
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            setMaintenanceAcknowledged(false);
        } else if (activeMaintenance) {
            // 점검 ID가 변경되었는지 확인
            const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
            if (acknowledgedSeqNo && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
                sessionStorage.removeItem('maintenance_acknowledged');
                sessionStorage.removeItem('maintenance_acknowledged_seqno');
                setMaintenanceAcknowledged(false);
            }
        }
    }, [isMaintenance, activeMaintenance]);

    // 오늘 하루 보지 않기 처리된 공지 ID 가져오기
    const getHiddenNoticeIds = (): number[] => {
        const today = new Date().toISOString().split('T')[0];
        const stored = localStorage.getItem('hiddenNotices');
        if (!stored) return [];
        try {
            const data = JSON.parse(stored);
            if (data.date === today) {
                return data.ids || [];
            }
            return [];
        } catch {
            return [];
        }
    };

    // 오늘 하루 보지 않기 저장
    const hideNoticeForToday = (noticeId: number) => {
        const today = new Date().toISOString().split('T')[0];
        const hiddenIds = getHiddenNoticeIds();
        if (!hiddenIds.includes(noticeId)) {
            hiddenIds.push(noticeId);
        }
        localStorage.setItem('hiddenNotices', JSON.stringify({ date: today, ids: hiddenIds }));
    };

    // 공지사항 팝업 로드
    useEffect(() => {
        const fetchPopupNotices = async () => {
            try {
                const response = await getMobileUnreadPopupNotices();
                const hiddenIds = getHiddenNoticeIds();
                const filteredNotices = (response.notices || []).filter(
                    (notice) => !hiddenIds.includes(notice.noticeId)
                );
                setPopupNotices(filteredNotices);
            } catch (error) {
                console.error('팝업 공지 조회 실패:', error);
            }
        };
        fetchPopupNotices();
    }, []);

    // 공지사항 팝업 닫기
    const handleCloseNotice = async () => {
        const currentNotice = popupNotices[currentNoticeIndex];
        if (currentNotice) {
            try {
                await markMobileNoticeAsRead(currentNotice.noticeId);
            } catch (error) {
                console.error('공지 읽음 처리 실패:', error);
            }
        }
        if (currentNoticeIndex < popupNotices.length - 1) {
            setCurrentNoticeIndex(prev => prev + 1);
        } else {
            setPopupNotices([]);
            setCurrentNoticeIndex(0);
        }
    };

    // 오늘 하루 보지 않기
    const handleHideForToday = (noticeId: number) => {
        hideNoticeForToday(noticeId);
        handleCloseNotice();
    };

    // URL 파라미터에서 탭 읽기
    useEffect(() => {
        const tab = searchParams.get('tab') as TabType;
        if (tab === 'summary' || tab === 'detail') {
            setActiveTab(tab);
        }
    }, [searchParams]);

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const goToMyPage = () => {
        navigate('/my-page');
    };

    // 점검 화면 확인 처리
    const handleMaintenanceAcknowledge = () => {
        if (activeMaintenance) {
            sessionStorage.setItem('maintenance_acknowledged', 'true');
            sessionStorage.setItem('maintenance_acknowledged_seqno', String(activeMaintenance.seqNo));
            setMaintenanceAcknowledged(true);
        }
    };

    // 디버깅: 점검 상태 로그
    useEffect(() => {
        console.log('DeliveryPage - isMaintenance:', isMaintenance, 'activeMaintenance:', activeMaintenance, 'maintenanceLoading:', maintenanceLoading);
    }, [isMaintenance, activeMaintenance, maintenanceLoading]);

    // 점검 데이터 로딩 중이면 대기
    if (maintenanceLoading) {
        return null; // 또는 로딩 스피너
    }

    // 점검 중이고 아직 확인하지 않았으면 MaintenanceScreen 전체 화면 표시
    if (isMaintenance && activeMaintenance && !maintenanceAcknowledged) {
        return (
            <MaintenanceScreen 
                maintenance={activeMaintenance} 
                showDashboardButton={true}
                onDashboardClick={handleMaintenanceAcknowledge}
            />
        );
    }

    // 로딩 중이 아니고 협력사인 경우 HomePageForSupplier 렌더링
    if (!loading && isSupplier) {
        return <HomePageForSupplier />;
    }

    return (
        <div className={`screen active ${styles['delivery-screen']}`}>
            {/* 헤더 */}
            <div className={styles['delivery-header']}>
                <div className={styles['header-top']}>
                    <h1 className={styles['page-title']}>
                        <span className={styles['greeting-name']}>{riderName}</span>님 안녕하세요
                    </h1>
                    <div className={styles['header-actions']}>
                        <BalanceDisplay />
                        <button className={styles['settings-btn']} onClick={goToMyPage}>
                            <i className="fas fa-cog"></i>
                        </button>
                    </div>
                </div>
                
                {/* 탭 */}
                <div className={styles['tabs']}>
                    <button
                        className={`${styles['tab']} ${activeTab === 'summary' ? styles['tab-active'] : ''}`}
                        onClick={() => handleTabChange('summary')}
                    >
                        요약
                    </button>
                    <button
                        className={`${styles['tab']} ${activeTab === 'detail' ? styles['tab-active'] : ''}`}
                        onClick={() => handleTabChange('detail')}
                    >
                        상세
                    </button>
                </div>
            </div>

            {/* 탭 컨텐츠 */}
            <div className={styles['tab-content']}>
                {activeTab === 'summary' ? (
                    <DeliverySummaryTab />
                ) : (
                    <DeliveryDetailTab />
                )}
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="delivery" />

            {/* 공지사항 팝업 */}
            {popupNotices.length > 0 && popupNotices[currentNoticeIndex] && (
                <NoticePopupModal
                    notice={popupNotices[currentNoticeIndex]}
                    zIndex={1000 + (popupNotices.length - currentNoticeIndex)}
                    onClose={handleCloseNotice}
                    onHideForToday={handleHideForToday}
                />
            )}
        </div>
    );
};

export default DeliveryPage;
